import React, { useState, useEffect } from 'react';
import {
  Table,
  Modal,
  Input,
  Button,
  Card,
  Typography,
  Space,
  message,
  Spin,
  List,
  Avatar,
  Divider
} from 'antd';
import { EyeOutlined, UserOutlined, RobotOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { config } from '../code/vars';

const { Title, Text, Paragraph } = Typography;
const { Password } = Input;

interface ChatSession {
  session_id: string;
  user_id: string | null;
  expert_id: number;
  message_count: number;
  last_message_id: number;
}

interface ChatMessage {
  id: number;
  user_id: string | null;
  expert_id: number;
  query: string;
  response: string;
  history: any;
  session_id: string;
  session_message_count: number;
}

const AdminChatHistory: React.FC = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const authenticate = async () => {
    if (!password) {
      message.error('Please enter admin password');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${config.api_base}/qa/admin/chat-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data);
        setIsAuthenticated(true);
        message.success('Authentication successful');
      } else {
        message.error('Invalid admin password');
      }
    } catch (error) {
      message.error('Authentication failed');
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionMessages = async (sessionId: string) => {
    if (!password) return;

    try {
      setMessagesLoading(true);
      const response = await fetch(`${config.api_base}/qa/admin/chat-session/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        setSelectedSession(sessionId);
        setSessionModalVisible(true);
      } else {
        message.error('Failed to load session messages');
      }
    } catch (error) {
      message.error('Failed to load session messages');
      console.error('Load messages error:', error);
    } finally {
      setMessagesLoading(false);
    }
  };

  const columns: ColumnsType<ChatSession> = [
    {
      title: 'Session ID',
      dataIndex: 'session_id',
      key: 'session_id',
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      render: (text) => text ? <Text code>{text}</Text> : <Text type="secondary">Anonymous</Text>,
    },
    {
      title: 'Expert ID',
      dataIndex: 'expert_id',
      key: 'expert_id',
    },
    {
      title: 'Message Count',
      dataIndex: 'message_count',
      key: 'message_count',
    },
    {
      title: 'Last Message ID',
      dataIndex: 'last_message_id',
      key: 'last_message_id',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => loadSessionMessages(record.session_id)}
          loading={messagesLoading && selectedSession === record.session_id}
        >
          View Messages
        </Button>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Card style={{ width: 400 }}>
          <Title level={3} style={{ textAlign: 'center', marginBottom: 24 }}>
            Admin Authentication
          </Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Password
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPressEnter={authenticate}
            />
            <Button
              type="primary"
              block
              onClick={authenticate}
              loading={loading}
            >
              Login
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Admin Chat History</Title>
      <Paragraph type="secondary">
        View and manage all chat sessions and messages
      </Paragraph>

      <Table
        columns={columns}
        dataSource={sessions}
        rowKey="session_id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} sessions`,
        }}
      />

      <Modal
        title={`Chat Session: ${selectedSession}`}
        open={sessionModalVisible}
        onCancel={() => setSessionModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSessionModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <Spin spinning={messagesLoading}>
          <List
            itemLayout="vertical"
            dataSource={messages}
            renderItem={(message, index) => (
              <div key={message.id}>
                <List.Item>
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                    title={<Text strong>User Query #{message.session_message_count}</Text>}
                    description={
                      <div>
                        <Text type="secondary">User ID: {message.user_id || 'Anonymous'}</Text>
                        <br />
                        <Text type="secondary">Message ID: {message.id}</Text>
                      </div>
                    }
                  />
                  <Paragraph>{message.query}</Paragraph>
                </List.Item>

                <List.Item style={{ marginTop: -16 }}>
                  <List.Item.Meta
                    avatar={<Avatar icon={<RobotOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                    title={<Text strong>AI Response</Text>}
                    description={<Text type="secondary">Expert ID: {message.expert_id}</Text>}
                  />
                  <Paragraph>{message.response}</Paragraph>
                </List.Item>

                {index < messages.length - 1 && <Divider />}
              </div>
            )}
          />
        </Spin>
      </Modal>
    </div>
  );
};

export default AdminChatHistory;